// The signed-out pages beside the login card (legacy ForgotPassword, PasswordReset and AcceptInvite in AlternateLayout):
// backoffice.auth.forgot-password, backoffice.auth.password-reset and backoffice.invite.accept (the /accounts list is
// features/accounts). Compared with legacy side by side at 1280×800 on local DDEV (D6 round 34; field note
// backoffice.auth.signed-out-pages): each is legacy's 400 px card with its words, its toasts and where it goes next.
// Every call goes through the session transport (`/v1/auth/*`); links arrive by email only.
//
// Where legacy is fixed (field-notes/bugs.md, ledger FIX rows):
// - Forgot password said its sentence twice, and Continue did nothing, silently, for an empty or malformed email. The
//   server answers every email the same way (SEC-51), so an email without an account gets the same toast and Log in,
//   not legacy's "Provided email does not exist in our records".
// - Reset answered an empty or short password with "Unable to process request"; here the field says why.
// - A used, expired or unknown invitation left a blank page once its toast faded; here the card says so.
// - Opening an invitation link accepted it for someone with an account; here they press Accept Invite (B397).
// Next's own: a link invite (the inviter was shown its link) first asks for the code mailed to the invited address
// (A191, ops plan 2026-10-24-invite-email-verification). Legacy mails every invite, so it has no such step.
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { INVITE_CODE_LENGTH, INVITE_CODE_RESEND_AFTER_MS, INVITE_CODE_TTL_MS, type InviteSummary } from '@radish/contracts';
import { AuthLayout, Button, LoadingState, TextField, useToast } from '@radish/ui-web';
import { useApi } from '../../data/api.tsx';
import { endSession } from '../../session/session.ts';
import { phoneText } from '../branding/LocationInfoPage.tsx';
import { AUTH_LINK, AuthCard, AuthTitle, ErrorAlert, PasswordField } from './fields.tsx';
import { EMAIL_PATTERN } from '../../lib/email.ts';

const PASSWORD_MIN = 8;
/** The server's own sentence for a short password (ledger L512: one rule for every password Radish Next stores). */
const TOO_SHORT = 'The password must be at least 8 characters.';

/** Legacy's document titles: "<page> - Back Office". */
function useTitle(title: string): void {
  useEffect(() => {
    document.title = `${title} - Back Office`;
  }, [title]);
}

/** A new password's field rules: legacy's required sentence, and the eight characters the server asks for. */
function newPasswordProblem(password: string): string | undefined {
  if (password === '') return 'Please enter password';
  if (password.length < PASSWORD_MIN) return TOO_SHORT;
  return undefined;
}

export function ForgotPasswordPage() {
  useTitle('Reset Password');
  const { auth } = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const typed = email.trim();
    if (typed === '') return setError('Please type an email');
    if (!EMAIL_PATTERN.test(typed)) return setError('Please enter a valid email address');
    setError(undefined);
    setBusy(true);
    const result = await auth.forgotPassword(typed);
    setBusy(false);
    if (!result.ok) return toast.error(result.error.message);
    // The same answer for every email (SEC-51): legacy's toast, worded so it is true for an email without an account.
    toast.success('If that email has an account, we have emailed your password reset link!');
    setEmail('');
    await navigate({ to: '/login', search: {} });
  };

  return (
    <AuthLayout logoSrc="/radish-logo.svg" darkLogoSrc="/radish-logo-dark.svg">
      <AuthCard testId="forgot-card">
        <div className="text-[14px] leading-[21px] font-bold text-text-2 uppercase">Password Reset</div>
        <AuthTitle className="mb-6">Forgot password?</AuthTitle>
        <p className="text-[14px] leading-[21px] text-text-2">Enter the email address associated with your account and we&apos;ll send you a link to reset your password.</p>
        <form noValidate onSubmit={(e) => { e.preventDefault(); void submit(); }} data-testid="forgot-form">
          {/* Legacy lower-cases the address as it is typed. */}
          <TextField label="Email" type="email" value={email} onValueChange={(v) => setEmail(v.toLowerCase())} error={error} autoComplete="username" />
          <Button type="submit" variant="auth" loading={busy}>Continue</Button>
        </form>
        <Link to="/login" search={{}} className={AUTH_LINK}>Return to Log in</Link>
      </AuthCard>
    </AuthLayout>
  );
}

export function ResetPasswordPage({ token }: { readonly token: string }) {
  useTitle('Password Reset');
  const { auth } = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const [valid, setValid] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void auth.checkResetToken(token).then((r) => {
      if (live) setValid(r.ok && r.data.valid);
    });
    return () => {
      live = false;
    };
  }, [auth, token]);

  const submit = async () => {
    const short = newPasswordProblem(password);
    const next = {
      ...(short === undefined ? {} : { password: short }),
      ...(confirm === '' ? { confirm: 'Please enter password' } : {}),
    };
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    // Legacy says a mismatch in a toast, not under a field.
    if (password !== confirm) return toast.error('Passwords do not match');
    setBusy(true);
    const result = await auth.resetPassword({ token, password });
    setBusy(false);
    if (result.ok) {
      toast.success('Your password has been reset!');
      await navigate({ to: '/login', search: {} });
      return;
    }
    if (result.error.code === 'invalid_token') return setValid(false);
    return toast.error('Unable to process request');
  };

  const body = () => {
    if (valid === null) return <LoadingState />;
    if (!valid) {
      return (
        <div className="p-4" data-testid="reset-invalid">
          <ErrorAlert>Password Reset token is invalid. Please try again!!</ErrorAlert>
        </div>
      );
    }
    return (
      <form noValidate className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void submit(); }} data-testid="reset-form">
        <PasswordField label="Password" value={password} onValueChange={setPassword} error={errors.password} autoComplete="new-password" />
        <PasswordField label="Confirm Password" value={confirm} onValueChange={setConfirm} error={errors.confirm} autoComplete="new-password" />
        <Button type="submit" variant="auth" loading={busy}>Reset Password</Button>
      </form>
    );
  };

  return (
    <AuthLayout logoSrc="/radish-logo.svg" darkLogoSrc="/radish-logo-dark.svg">
      <AuthCard testId="reset-card">
        <AuthTitle className={valid === false ? 'mb-12' : 'mb-8'}>Reset your Password</AuthTitle>
        {body()}
      </AuthCard>
    </AuthLayout>
  );
}

/** Legacy's outlined box that shows the invited email, read-only and without a label. */
function InvitedEmail({ email }: { readonly email: string }) {
  return (
    <div aria-label="Email" className="flex h-14 items-center rounded-q border border-line bg-field px-3 text-[14px] text-text-1" data-testid="invite-email">
      {email}
    </div>
  );
}

/** The invited address as the code step names it: its first letter and its domain ("s•••@owlclub.test"). */
export function maskedEmail(email: string): string {
  const at = email.lastIndexOf('@');
  return at <= 0 ? '•••' : `${email.slice(0, 1)}•••${email.slice(at)}`;
}

const CODE_PATTERN = new RegExp(`^[0-9]{${String(INVITE_CODE_LENGTH)}}$`);
/** When this tab last had a code sent for a link (so a reload inside the code's ten minutes sends no second mail). */
const sentKey = (token: string) => `radish.invite.codeSentAt.${token.slice(0, 16)}`;
function readSentAt(token: string): number | null {
  try {
    const v = Number(sessionStorage.getItem(sentKey(token)));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}
function writeSentAt(token: string, at: number): void {
  try {
    sessionStorage.setItem(sentKey(token), String(at));
  } catch {
    // Private windows may refuse storage; a reload then just sends another code.
  }
}

/**
 * A link invite's first step (A191): the code is mailed as the page opens (no button to ask for it), the person types
 * it, and Resend offers a new one after a minute. Accept checks the code; a refusal brings the person back here.
 */
function InviteCodeStep({ token, email, code, onCode, error, onContinue }: {
  readonly token: string; readonly email: string; readonly code: string; readonly onCode: (code: string) => void;
  readonly error: string | undefined; readonly onContinue: () => void;
}) {
  const { auth } = useApi();
  const toast = useToast();
  const [sentAt, setSentAt] = useState<number | null>(() => readSentAt(token));
  const [now, setNow] = useState(() => Date.now());
  const [sending, setSending] = useState(false);
  const asked = useRef(false);

  const send = async (again: boolean) => {
    setSending(true);
    const result = await auth.sendInviteCode(token);
    setSending(false);
    if (!result.ok) return toast.error(result.error.message);
    const at = Date.now();
    writeSentAt(token, at);
    setSentAt(at);
    setNow(at);
    if (again) toast.success('We sent a new code.');
  };

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    const last = readSentAt(token);
    if (last === null || Date.now() - last >= INVITE_CODE_TTL_MS) void send(false);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const waitMs = sentAt === null ? 0 : Math.max(0, sentAt + INVITE_CODE_RESEND_AFTER_MS - now);
  return (
    <form noValidate className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); onContinue(); }} data-testid="invite-code">
      <p className="text-[14px] leading-[21px] text-text-2" data-testid="invite-code-sent">
        We sent a code to {maskedEmail(email)}. Enter it to accept the invite.
      </p>
      <TextField label="Code" inputMode="numeric" autoComplete="one-time-code" value={code}
        onValueChange={(v) => onCode(v.replace(/\D/g, '').slice(0, INVITE_CODE_LENGTH))} error={error} />
      <Button type="submit" variant="auth">Continue</Button>
      <button type="button" className={`${AUTH_LINK} disabled:cursor-not-allowed disabled:opacity-60 disabled:no-underline`} disabled={waitMs > 0 || sending}
        onClick={() => void send(true)}>
        {waitMs > 0 ? `Resend code in ${String(Math.ceil(waitMs / 1000))} s` : 'Resend code'}
      </button>
    </form>
  );
}

export function AcceptInvitePage({ token }: { readonly token: string }) {
  useTitle('User Invite');
  const { auth } = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InviteSummary | null | undefined>(undefined);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{ name?: string; phone?: string; password?: string; confirm?: string }>({});
  const [busy, setBusy] = useState(false);
  // A191: a link invite's code, and whether the person has moved past the code step.
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | undefined>();
  const [codeEntered, setCodeEntered] = useState(false);
  const needsCode = invite?.verify === 'code';
  const withCode = needsCode ? { code } : {};

  /** The server turned the code away (wrong, voided or expired): back to the code step, saying why; the details stay. */
  const codeRefused = (codeName: string, message: string): boolean => {
    if (codeName !== 'code_invalid' && codeName !== 'code_expired' && codeName !== 'code_required') return false;
    setCodeEntered(false);
    setCodeError(message);
    return true;
  };
  const continueWithCode = () => {
    if (!CODE_PATTERN.test(code)) return setCodeError(`Enter the ${String(INVITE_CODE_LENGTH)}-digit code.`);
    setCodeError(undefined);
    setCodeEntered(true);
  };

  /** Legacy, once an invitation is accepted: its toast, the session ended, and Log in. */
  const accepted = async (message: string) => {
    toast.success(message);
    await endSession();
    await navigate({ to: '/login', search: {} });
  };

  useEffect(() => {
    let live = true;
    void auth.checkInvite(token).then((r) => {
      if (live) setInvite(r.ok ? r.data.invite : null);
    });
    return () => {
      live = false;
    };
  }, [auth, token]);

  /**
   * Someone who already has an account accepts with one press. Legacy accepted as soon as the link was opened (a GET),
   * so a mail scanner or link preview fetching it could join them to a business they never chose (B397, L556).
   */
  const acceptExisting = async () => {
    setBusy(true);
    const result = await auth.acceptInvite({ token, ...withCode });
    setBusy(false);
    if (result.ok) return accepted('Invite accepted successfully');
    if (result.error.code === 'invalid_invite') return setInvite(null);
    if (codeRefused(result.error.code, result.error.message)) return;
    return toast.error(result.error.message);
  };

  const submit = async () => {
    const digits = phone.replace(/\D/g, '');
    const short = newPasswordProblem(password);
    const next = {
      ...(name.trim() === '' ? { name: 'Please type a name' } : {}),
      ...(digits !== '' && digits.length !== 10 ? { phone: 'Phone number must be 10 digits' } : {}),
      ...(short === undefined ? {} : { password: short }),
      ...(confirm === '' ? { confirm: 'Confirm New Password is required' } : confirm !== password ? { confirm: 'Passwords do not match' } : {}),
    };
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setBusy(true);
    const result = await auth.acceptInvite({ token, name: name.trim(), password, ...(digits === '' ? {} : { phone: digits }), ...withCode });
    setBusy(false);
    if (result.ok) return accepted('User created successfully');
    if (result.error.code === 'invalid_invite') return setInvite(null);
    if (codeRefused(result.error.code, result.error.message)) return;
    return toast.error(result.error.message);
  };

  if (invite === undefined) return <AuthLayout logoSrc="/radish-logo.svg" darkLogoSrc="/radish-logo-dark.svg"><LoadingState /></AuthLayout>;
  return (
    <AuthLayout logoSrc="/radish-logo.svg" darkLogoSrc="/radish-logo-dark.svg">
      <AuthCard testId="invite-card">
        <h1 className="mb-4 text-[20.8px] leading-8 text-text-2">Register</h1>
        {invite !== null && needsCode && !codeEntered ? (
          <InviteCodeStep token={token} email={invite.email} code={code} onCode={setCode} error={codeError} onContinue={continueWithCode} />
        ) : invite !== null && invite.existingUser ? (
          // Legacy's accepted card (a centred column, the message in heading-lg, one button), asking first.
          <div className="flex flex-col items-center gap-4 text-center" data-testid="invite-existing">
            <p className="text-[20.8px] leading-8 text-text-2">You have been invited to {invite.locationName}.</p>
            <Button variant="auth" loading={busy} onClick={() => void acceptExisting()}>Accept Invite</Button>
          </div>
        ) : invite === null ? (
          <div className="flex flex-col gap-4" data-testid="invite-invalid">
            <ErrorAlert>This invite is invalid, has expired or was already accepted.</ErrorAlert>
            <Link to="/login" search={{}} className={AUTH_LINK}>Return to Log in</Link>
          </div>
        ) : (
          <form noValidate className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void submit(); }} data-testid="invite-form">
            <InvitedEmail email={invite.email} />
            <TextField label="Name" value={name} onValueChange={setName} error={errors.name} autoComplete="name" />
            <TextField label="Phone" inputMode="tel" value={phoneText(phone)} onValueChange={(v) => setPhone(v.replace(/\D/g, '').slice(0, 10))} error={errors.phone} autoComplete="tel" />
            <PasswordField label="Password" value={password} onValueChange={setPassword} error={errors.password} autoComplete="new-password" mutedEye />
            <PasswordField label="Confirm Password" value={confirm} onValueChange={setConfirm} error={errors.confirm} autoComplete="new-password" mutedEye />
            {/* Legacy: 12 px above Submit, where the fields are 16 px apart. */}
            <Button type="submit" variant="auth" loading={busy} className="-mt-1!">Submit</Button>
          </form>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
